import React from 'react';
import styled from 'styled-components/macro';
// eslint-disable-next-line no-restricted-imports
import { css } from 'styled-components';
import { Color } from 'lib/modelBuilder/entities/Color';
import Img from 'components/Img';
import { useTranslation } from 'react-i18next';
import { PaneMaterial } from 'lib/modelBuilder/constants';
import { FiPlusSquare } from 'react-icons/fi';
import ReactTooltip from 'react-tooltip';

export enum SelectorSize {
  extraSmall = 'xs',
  small = 'sm',
  medium = 'md',
  large = 'lg',
}

interface Props {
  title: string;
  shortTitle?: string | boolean;
  color?: Color;
  selected?: boolean;
  imgSrc?: string;
  onClick?: any;
  canAddNew: boolean;
  present: boolean;
  size?: SelectorSize;
  rounded?: boolean;
  showMaterial?: boolean;
  glass?: boolean;
  showColor?: boolean;
}

const GenericObjectSelector = ({
  title,
  shortTitle,
  color,
  selected,
  imgSrc,
  onClick,
  present,
  canAddNew,
  size = SelectorSize.small,
  rounded,
  showMaterial = true,
  glass,
  showColor,
}: Props) => {
  const { t } = useTranslation();

  let src: string | undefined = undefined;
  if (imgSrc) {
    src = imgSrc;
  } else if (color && color.texture) {
    src = color.texture;
  }

  const getImg = () => {
    if (src) {
      return <StyledPreviewImage src={src} alt={title} size={size} />;
    } else if (color) {
      return (
        <StyledColorPreview
          color={color.hexaHtml}
          rounded={rounded}
          size={size}
        />
      );
    }
    return null;
  };

  const materialName = (material: PaneMaterial): string => {
    switch (material) {
      case PaneMaterial.MDF:
        return t('Mdf');
      case PaneMaterial.Frost:
        return t('short.acrylic');
      case PaneMaterial.FrostOpaque:
        return t('short.acrylic');
      default:
        return '';
    }
  };

  let colorName: string | null = null;
  if (color && size === SelectorSize.small) {
    colorName = color.name;
    if (color?.material) {
      colorName = `${materialName(color.material)} ${colorName}`;
    }
  }

  return (
    <StyledGenericObjectSelectorBtn
      onClick={onClick}
      size={size}
      selected={selected}
      data-tip={colorName}
      data-for="panelToolTip"
    >
      <div className="self-center">{shortTitle ? shortTitle : title}</div>
      <div className="self-center flex">
        {present ? (
          getImg()
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <FiPlusSquare
              className={`h-${size === SelectorSize.small ? 8 : 12} w-${
                size === SelectorSize.small ? 8 : 12
              }`}
            />
          </div>
        )}
      </div>
      {size && (size === SelectorSize.large || size === SelectorSize.medium) && (
        <div className="text-sm italic">
          {present ? (
            color ? (
              <>
                {showMaterial &&
                  color?.material &&
                  materialName(color.material)}{' '}
                {color?.name} {glass ? ` + ${t('Glass')}` : ''}
              </>
            ) : (
              <div>{showColor ? t('Various colors') : ''}</div>
            )
          ) : (
            t('Add')
          )}
        </div>
      )}
      <ReactTooltip id="panelToolTip" place="bottom" type="dark" />
    </StyledGenericObjectSelectorBtn>
  );
};

export default GenericObjectSelector;

const StyledGenericObjectSelectorBtn = styled.button<{
  size?: SelectorSize;
  selected?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: solid 1px ${props => props.theme.gray300};
  background-color: ${p => p.theme.white};
  padding: 0 0.5rem;
  border-radius: 0.25rem;
  color: ${p => p.theme.main};
  font-size: 0.875rem;

  @media (min-width: 375px) {
    margin: 0 0.5rem 0.5rem;
  }

  ${p =>
    p.size === SelectorSize.small &&
    css`
      margin: 0.25rem 0.75rem 0.25rem;
      padding: 0.3rem 0.5rem;
      font-weight: 400;
      font-size: 1rem;
      flex-direction: row;
    `}

  ${p =>
    p.size === SelectorSize.medium &&
    css`
      margin: 0 0 0.5rem;
    `}
 
  
  &:hover {
    background-color: ${p => p.theme.hover};
  }

  ${p =>
    p.selected &&
    css`
      border: none !important;
      color: white !important;
      background-color: ${props => props.theme.main} !important;
      font-weight: 500 !important;
    `}
`;

const StyledColorPreview = styled.div<{
  color?: string;
  rounded?: boolean;
  size: SelectorSize;
}>`
  
  
  border-radius: ${p => (p.rounded ? '9999px' : '0px')};
  border-color: ${p => p.theme.gray400};
  border-width: 1px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  width: 28px;
  height: 28px;
  
  background-color: ${p => (p.color ? p.color : 'inherit')};
  
  ${p =>
    p.color &&
    css`
      background-color: ${p.color};
    `}
  
  ${p =>
    p.size === SelectorSize.small &&
    css`
      margin-left: 0.5rem;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1),
        0 1px 2px 0 rgba(0, 0, 0, 0.06);
    `}

  ${p =>
    p.size === SelectorSize.medium &&
    css`
      width: 3rem;
      height: 3rem;
    `}
  ${p =>
    p.size === SelectorSize.large &&
    css`
      height: 4rem;
      width: 4rem;
    `}
`;

const StyledPreviewImage = styled(Img)<{ size?: SelectorSize }>`
  width: 3rem;
  height: 3rem;

  ${p =>
    p.size === SelectorSize.small &&
    css`
      margin-left: 0.5rem;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1),
        0 1px 2px 0 rgba(0, 0, 0, 0.06);
      width: 28px;
      height: 28px;
    `}

  ${p =>
    p.size === SelectorSize.large &&
    css`
      height: 4rem;
      width: 4rem;
    `}
`;
